import React from 'react'
import AntdSpace from 'antd/es/space'
import type { SpaceProps } from 'antd/es/space'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { Space as ShadcnSeamSpace } from './Space.shadcn'

type SpaceCompactProps = React.ComponentProps<typeof AntdSpace.Compact>

/**
 * Compat seam leaf for antd's `antd/es/space` — and, with `./Typography`, the
 * SECOND and THIRD components put behind the design toggle (after `./Button`).
 *
 * A LEAF, not the `src/antd` barrel, on purpose (PRO-130): the barrel's
 * `export * from 'antd'` pulls antd/es/index.js — the whole component barrel —
 * into whatever chunk imports it, and the call sites for this module are
 * boot-static. `update-design` ships a real seam at this same path, with the
 * same shape — a named `Space` AND a default — so a redirected call site needs
 * no further change when that lands. The seam leaves mirror the merge target
 * per module, so which of the two shapes a leaf carries differs on purpose:
 * `Button`/`Result`/`Skeleton` are named-only there.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch this component renders antd's own
 * Space with the props it was given and the ref it was given: same element,
 * same class names, same DOM. The only difference from
 * `export { default } from 'antd/es/space'` — which is what this file was — is
 * one extra component in the React tree, which renders nothing of its own.
 * `__tests__/space-seam-parity.test.tsx` asserts that byte for byte across a
 * prop matrix, and it is the test to keep green: `development` continuing to
 * ship antd unchanged to customers is the whole premise of shipping shadcn one
 * component at a time.
 *
 * WHY SPACE WAS CHOSEN SECOND. Reach and risk, in that order. 601 files import
 * `Space` from `src/antd` and 1,700 JSX tags render it, so it is the second
 * widest surface in the seam after Typography — flipping the control repaints
 * the gaps on nearly every screen at once. And it carries no popup and no
 * portal: it is one `<div>` of flex layout, so there is no stacking context,
 * no `getPopupContainer` and no measurement to get wrong. The reference
 * branch's visual-defect survey records no open defect against it.
 *
 * antd's one static is carried across explicitly, NOT by copying antd's
 * component object: `Object.assign(dispatcher, AntdSpace)` would also copy a
 * forwardRef object's `$$typeof` and `render` and turn this into something
 * React cannot render. `Compact` dispatches on the mode exactly as Space
 * itself does, so the seam cannot end up half-switched.
 * ------------------------------------------------------------------------- */
const SpaceDispatcher = React.forwardRef<HTMLDivElement, SpaceProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn') {
      return <ShadcnSeamSpace {...props} ref={ref} />
    }

    return <AntdSpace {...props} ref={ref} />
  }
)

SpaceDispatcher.displayName = 'Space'

// antd's `Space.Compact` is a plain function component, not a forwardRef, so
// the dispatcher must not take a ref either — mirroring the merge target's
// shape is what keeps a call site working either way.
const SpaceCompactDispatcher = (props: SpaceCompactProps) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamSpace.Compact {...props} />

  return <AntdSpace.Compact {...props} />
}

SpaceCompactDispatcher.displayName = 'Space.Compact'

export const Space = Object.assign(SpaceDispatcher, {
  Compact: SpaceCompactDispatcher,
})

export default Space
